import React from 'react';
import { motion } from 'framer-motion';
import { Star, Quote, Sparkles } from 'lucide-react';
import { Card } from '../ui';
import { getFeaturedTestimonials } from '../../data/testimonials';

const Testimonials = () => {
  const testimonials = getFeaturedTestimonials();

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  const renderStars = (rating) => {
    return [...Array(5)].map((_, i) => (
      <motion.span
        key={i}
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: i * 0.1, type: "spring", stiffness: 300 }}
      >
        <Star
          className={`w-4 h-4 ${i < rating
              ? 'text-amber-400 fill-amber-400 drop-shadow-sm'
              : 'text-warm-gray/20'
            }`}
        />
      </motion.span>
    ));
  };

  return (
    <section className="py-24 bg-gradient-to-b from-soft-white to-pragyavan-cream/20 relative overflow-hidden">
      {/* Decorative Background */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-0 w-64 h-64 bg-pragyavan-gold/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-0 w-96 h-96 bg-sage-green/5 rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-20"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-pragyavan-gold/10 text-pragyavan-brown text-xs font-medium mb-4">
            <Sparkles className="w-3 h-3" />
            <span>Community Stories</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-display font-bold text-pragyavan-brown mb-6">
            Voices of <span className="text-gradient-gold">Transformation</span>
          </h2>
          <p className="text-lg text-warm-gray max-w-2xl mx-auto leading-relaxed">
            Discover how Pragyavan has transformed lives and brought peace,
            wellness, and mindfulness to our community.
          </p>
        </motion.div>

        {/* Testimonials Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={testimonial.id}
              variants={itemVariants}
              whileHover={{ y: -8, scale: 1.01 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className="group"
            >
              <Card
                className="h-full relative bg-white/70 backdrop-blur-sm border border-white/60 shadow-glass hover:shadow-premium hover:bg-white/90 transition-all duration-300"
                padding="large"
                rounded="large"
              >
                {/* Quote Icon */}
                <div className="absolute top-6 right-6 text-pragyavan-gold/10 group-hover:text-pragyavan-gold/20 transition-colors duration-300">
                  <Quote className="w-12 h-12" />
                </div>

                <Card.Body>
                  {/* Rating */}
                  <div className="flex items-center mb-6 bg-white/50 w-fit px-3 py-1 rounded-full border border-white/50">
                    {renderStars(testimonial.rating)}
                    <span className="ml-2 text-xs font-medium text-pragyavan-brown/60">
                      {testimonial.rating}.0
                    </span>
                  </div>

                  {/* Testimonial Content */}
                  <blockquote className="text-pragyavan-brown/70 leading-relaxed mb-8 text-lg font-display text-pretty">
                    "{testimonial.content}"
                  </blockquote>

                  {/* Author */}
                  <div className="flex items-center mt-auto pt-6 border-t border-pragyavan-brown/5">
                    {/* Avatar */}
                    {testimonial.image ? (
                      <div className="w-12 h-12 rounded-full overflow-hidden mr-4 flex-shrink-0 shadow-inner ring-2 ring-pragyavan-gold/20">
                        <img 
                          src={testimonial.image} 
                          alt={testimonial.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-pragyavan-gold/20 to-sage-green/20 flex items-center justify-center mr-4 flex-shrink-0 shadow-inner">
                        <span className="text-pragyavan-brown font-bold text-lg">
                          {testimonial.name.charAt(0)}
                        </span>
                      </div>
                    )}

                    <div>
                      <div className="font-bold text-pragyavan-brown">
                        {testimonial.name}
                      </div>
                      <div className="text-sm text-pragyavan-gold font-medium">
                        {testimonial.role}
                      </div>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Testimonials;
